import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";
import { Typography, Box } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import moment from "moment";
import { useAuth } from "../../../../contexts/AuthContext";
import PeriodSelector from "../../../global/PeriodSelector";
import AgentReport from "./shared/agent-report/agent-report.shared";
import * as API from "../../../../api";

const useStyles = makeStyles(theme => ({
  statusChip: {
    fontSize: "0.75rem",
    height: "20px",
  },
  activatedUnpaid: {
    backgroundColor: "#ff9800",
    color: "#fff",
    fontWeight: "bold",
  },
  notActivated: {
    backgroundColor: "#9e9e9e",
    color: "#fff",
  },
  summaryBox: {
    display: "flex",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: "wrap",
  },
  summaryItem: {
    textAlign: "center",
    padding: theme.spacing(1),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    minWidth: "120px",
  },
}));

/**
 * Displays a report of orders with unpaid commissions for a given dealer code, supplier, and date range.
 * Shows orders that have been activated but commissions haven't been paid, with tags for those not yet activated.
 *
 * @param {object} props - The component props.
 * @param {string} props.supplierPrettyName - The supplier name.
 * @param {boolean} props.impersonation - Whether the component is being impersonated.
 * @param {string} props.impersonationName - The name of the impersonated user.
 */
export default function CommissionNotPaidReport({
  supplierPrettyName,
  impersonation,
  impersonationName,
}) {
  const classes = useStyles();

  // Retrieve the current user from the authentication context
  const { currentUser, authSession } = useAuth();

  // Get supplierId from params
  const { supplierId } = useParams();

  // Set the initial dealer code to the current user's dealer code
  const [dealerCode, setDealerCode] = useState(currentUser.id);

  // Set the initial parameters, loading state, data, and customer modal state
  const [showLoading, setShowLoading] = useState(false);
  const [data, setData] = useState([]);

  // Set the initial start and end dates to the current month
  const [startDate, setStartDate] = useState(
    moment().startOf("month").format("YYYY-MM-DD")
  );
  const [endDate, setEndDate] = useState(
    moment().endOf("month").format("YYYY-MM-DD")
  );

  // Summary counters
  const [activatedUnpaid, setActivatedUnpaid] = useState(0);
  const [notActivated, setNotActivated] = useState(0);

  /**
   * Fetches the data from the server.
   * @returns {Promise<void>} - A promise that resolves when the data is fetched.
   */
  const getData = async () => {
    setShowLoading(true);
    // Call the server API to get the commission not paid report
    const { data: orderData, error } = await API.getOrderReport({
      user_id: dealerCode,
      supplier_id: supplierId,
      start_date: startDate,
      end_date: endDate,
      access_token: authSession.access_token,
      report: "commission_not_paid",
    });

    if (error) console.error(error);

    // Handle both old and new response formats
    const responseData = orderData?.data || orderData || [];
    setData(responseData);
    setShowLoading(false);
  };

  // Calculate summary counts when data changes
  useEffect(() => {
    if (data && data.length > 0) {
      const activated = data.filter(
        item => item.commission_status === "activated_unpaid"
      ).length;
      const notActivatedCount = data.filter(
        item => item.commission_status === "not_activated"
      ).length;

      setActivatedUnpaid(activated);
      setNotActivated(notActivatedCount);
    } else {
      setActivatedUnpaid(0);
      setNotActivated(0);
    }
  }, [data]);

  // Fetch data when the dealer code, start date, or end date changes
  useEffect(() => {
    if (dealerCode && startDate && endDate && supplierId) {
      getData();
    }
  }, [dealerCode, startDate, endDate, supplierId]);

  // Update the dealer code when the impersonation changes or when the component mounts
  useEffect(() => {
    if (impersonation) {
      setDealerCode(impersonation);
    } else {
      setDealerCode(currentUser.id);
    }
  }, [impersonation]);

  return (
    <div>
      {/* Display the dealer code and impersonation name */}
      <Typography variant="h6">
        Commission Not Paid Report for{" "}
        {impersonationName
          ? `: ${impersonationName}`
          : `: Z-${dealerCode} (You)`}
      </Typography>

      {/* Display the supplier name */}
      <Typography variant="h6">Service: {supplierPrettyName}</Typography>

      <div>
        {/* Display the current period */}
        <p>
          Current Period:{" "}
          <b>
            {startDate} - {endDate}
          </b>
        </p>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Showing orders activated in this period or created in this period (if
          not yet activated) where commissions have not been paid.
        </Typography>
      </div>

      {/* Render the period selector */}
      <PeriodSelector
        changeStartDate={date => setStartDate(date)}
        changeEndDate={date => setEndDate(date)}
      />

      {/* Summary boxes */}
      <Box className={classes.summaryBox}>
        <Box className={classes.summaryItem}>
          <Typography variant="h4" style={{ color: "#ff9800" }}>
            {activatedUnpaid}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Activated - Commission Due
          </Typography>
        </Box>
        <Box className={classes.summaryItem}>
          <Typography variant="h4" style={{ color: "#9e9e9e" }}>
            {notActivated}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Not Yet Activated
          </Typography>
        </Box>
        <Box className={classes.summaryItem}>
          <Typography variant="h4" color="primary">
            {activatedUnpaid + notActivated}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Total Outstanding
          </Typography>
        </Box>
      </Box>

      <AgentReport
        data={data}
        loading={showLoading}
        impersonation={impersonation}
      />
    </div>
  );
}
